'use client';

import Link from 'next/link';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined';
import Navigation from '../../../components/Navigation';
import {
    DocAnswer,
    DocBeforeAfter,
    DocCode,
    DocEyebrow,
    DocMistakeList,
    DocRelated,
    DocSection,
    DocSubtitle,
    DocText,
    DocTitle,
} from '../../../components/DocArticle';
import { encodeBase64 } from '../../../utils/base64';

const generatorUrl = (() => {
    const oldEncoded = encodeBase64(JSON.stringify({ name: 'Alice', age: 30 }, null, 2));
    const newEncoded = encodeBase64(JSON.stringify({ name: 'Alice', age: 31, active: true }, null, 2));
    return `/?old=${oldEncoded}&new=${newEncoded}`;
})();

const mistakes = [
    {
        mistake: 'Expecting the smallest possible patch',
        fix: 'Generators compare, they do not optimize. A moved array element usually becomes a remove plus an add, or a chain of replaces - correct, but not minimal. RFC 6902 defines what a patch means, not how to produce a small one.',
    },
    {
        mistake: 'Assuming two libraries emit the same patch',
        fix: 'Generation is not standardized. fast-json-patch, Python jsonpatch and other libraries can emit different - equally valid - patches for the same pair of documents.',
    },
    {
        mistake: 'Confusing a missing key with a null value',
        fix: '"active": null is a value; a missing "active" is not. A diff treats them differently: missing keys produce add or remove, a null produces replace.',
    },
    {
        mistake: 'Diffing reordered arrays and trusting the result blindly',
        fix: 'Most generators compare arrays index by index, so a reorder looks like "every element changed". The patch is valid but noisy - check it before shipping it.',
    },
];

export default function HowToGenerateJsonPatchPage() {
    return (
        <>
            <Navigation activePage="docs" />
            <main>
                <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6 }, px: { xs: 2, sm: 3 } }}>
                    {/* Breadcrumb */}
                    <Box component="nav" sx={{ display: 'flex', mb: 4, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>
                        <Link href="/" style={{ textDecoration: 'none', color: 'var(--fg-muted)' }}>
                            <Typography component="span" sx={{ '&:hover': { color: 'var(--accent)' }, fontSize: '0.875rem' }}>Home</Typography>
                        </Link>
                        <Typography component="span" sx={{ mx: 1, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>/</Typography>
                        <Link href="/docs" style={{ textDecoration: 'none', color: 'var(--fg-muted)' }}>
                            <Typography component="span" sx={{ '&:hover': { color: 'var(--accent)' }, fontSize: '0.875rem' }}>Documentation</Typography>
                        </Link>
                        <Typography component="span" sx={{ mx: 1, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>/</Typography>
                        <Typography component="span" sx={{ color: 'var(--fg)', fontSize: '0.875rem' }}>How to Generate a JSON Patch</Typography>
                    </Box>

                    <Box sx={{ maxWidth: '800px' }}>
                        <DocEyebrow>How-To</DocEyebrow>
                        <DocTitle>How to Generate a JSON Patch</DocTitle>
                        <DocSubtitle>
                            You have two versions of a JSON document and want the RFC 6902 patch that turns one into
                            the other. Here is how that generation works, what to watch out for, and how to do it in
                            the browser, JavaScript and Python.
                        </DocSubtitle>

                        <DocAnswer>
                            To generate a JSON Patch, diff the <strong>before</strong> document against
                            the <strong>after</strong> document: every added key becomes an <code>add</code>, every
                            removed key a <code>remove</code>, every changed value a <code>replace</code>. Paste both
                            documents into the <Link href="/" style={{ color: 'var(--accent)' }}>JSON Patch generator</Link> and
                            it emits the operations for you.
                        </DocAnswer>

                        <DocSection>A minimal example</DocSection>
                        <DocBeforeAfter
                            before={`{
  "name": "Alice",
  "age": 30
}`}
                            after={`{
  "name": "Alice",
  "age": 31,
  "active": true
}`}
                        />
                        <DocCode label="Generated patch">{`[
  { "op": "replace", "path": "/age", "value": 31 },
  { "op": "add", "path": "/active", "value": true }
]`}</DocCode>
                        <DocText>
                            One operation per change, each with a{' '}
                            <Link href="/docs/json-pointer" style={{ color: 'var(--accent)' }}>JSON Pointer</Link> path
                            to the value it touches.
                        </DocText>

                        <DocSection>How a generator decides the operations</DocSection>
                        <DocText>
                            The algorithm is a recursive walk of the two documents:
                        </DocText>
                        <Box component="ul" sx={{ color: 'var(--fg-muted)', lineHeight: 1.8, pl: 3, mb: 3 }}>
                            <li><strong>Objects</strong> are compared key by key: a key only in the after document is an <code>add</code>, only in the before is a <code>remove</code>, in both with different values recurses or becomes a <code>replace</code>.</li>
                            <li><strong>Arrays</strong> are usually compared index by index, which is why reordering produces noisy patches (see below).</li>
                            <li><strong>Equal subtrees</strong> produce nothing - untouched parts of the document never appear in the patch.</li>
                        </Box>

                        <DocSection>The array caveat</DocSection>
                        <DocText>
                            Index-by-index comparison means <code>["a", "b", "c"]</code> reordered to{' '}
                            <code>["c", "a", "b"]</code> diffs as three <code>replace</code> operations, not one{' '}
                            <code>move</code>. The patch is correct - applying it yields exactly the after document -
                            but it says more than a human would. If array ordering matters in your API, review
                            generated patches before sending them. More on array semantics in{' '}
                            <Link href="/docs/json-patch-array" style={{ color: 'var(--accent)' }}>JSON Patch and arrays</Link>,
                            and the raw line-level view in the <Link href="/diff" style={{ color: 'var(--accent)' }}>JSON diff tool</Link>.
                        </DocText>

                        <DocSection>Generating in code</DocSection>
                        <DocText>
                            The same diff is one call in the common libraries:
                        </DocText>
                        <DocCode label="JavaScript (fast-json-patch)">{`import { compare } from 'fast-json-patch';

const patch = compare(before, after);
// [{ op: 'replace', path: '/age', value: 31 },
//  { op: 'add', path: '/active', value: true }]`}</DocCode>
                        <DocCode label="Python (jsonpatch)">{`import jsonpatch

patch = jsonpatch.make_patch(before, after)
# same RFC 6902 operation list`}</DocCode>
                        <DocText>
                            Both are linked from our language guides:{' '}
                            <Link href="/guides/json-patch-in-javascript" style={{ color: 'var(--accent)' }}>JSON Patch in JavaScript</Link> and{' '}
                            <Link href="/guides/json-patch-in-python" style={{ color: 'var(--accent)' }}>JSON Patch in Python</Link>.
                        </DocText>

                        <DocSection>Common mistakes</DocSection>
                        <DocMistakeList items={mistakes} />

                        <DocSection>Reference</DocSection>
                        <DocText>
                            The operation semantics a generator targets are defined by{' '}
                            <a href="https://datatracker.ietf.org/doc/html/rfc6902#section-4" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>RFC 6902, section 4</a>.
                            Generation itself is not standardized - each library chooses its own diff strategy.
                        </DocText>

                        {/* CTA */}
                        <Box sx={{
                            mt: 6, p: 4, textAlign: 'center',
                            borderRadius: 'var(--radius-lg)',
                            background: 'var(--accent)', color: 'white',
                        }}>
                            <Typography variant="h2" sx={{ fontSize: '1.5rem', fontWeight: 600, mb: 2 }}>
                                Generate this patch yourself
                            </Typography>
                            <Typography sx={{ mb: 3, opacity: 0.9 }}>
                                Open the generator with the example above preloaded - edit either side and watch the patch change.
                            </Typography>
                            <Button
                                variant="contained"
                                size="large"
                                href={generatorUrl}
                                endIcon={<ArrowForwardIcon />}
                                sx={{
                                    backgroundColor: 'white', color: 'var(--accent)', fontWeight: 600,
                                    px: 4, py: 1.5, textTransform: 'none',
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                                }}
                            >
                                Open the generator with this example
                            </Button>
                        </Box>

                        <DocRelated links={[
                            { href: '/docs/how-to-apply-json-patch', title: 'How to apply a JSON Patch', description: 'The other half: executing the patch you generated' },
                            { href: '/docs/json-patch-errors', title: 'JSON Patch errors', description: 'Why operations fail and how to fix them' },
                            { href: '/docs/json-patch-array', title: 'JSON Patch and arrays', description: 'Why reordered arrays produce noisy patches' },
                            { href: '/diff', title: 'JSON diff tool', description: 'See the raw line-level difference under the patch' },
                        ]} />
                    </Box>
                </Container>
            </main>
        </>
    );
}
